// Pure helpers (no React) so they can be unit-tested on their own.

import type { Block } from '@twasta/page-schema'

// Bindings on a list block are keyed by POSITION (`buttons.2.on_click.sku`,
// `cards.0.title`). Reordering or deleting items without re-keying them left
// each binding on whatever item slid into its old slot. `order[newIdx] =
// oldIdx` describes the new list; bindings of dropped items are removed.
export function reindexListBindings<B extends Block>(block: B, listKey: string, order: number[]): B['bindings'] {
  const bindings = block.bindings
  if (!bindings) return bindings
  const toNew = new Map(order.map((oldIdx, newIdx) => [oldIdx, newIdx]))
  const prefix = `${listKey}.`
  const out: NonNullable<B['bindings']> = {}
  for (const [path, expr] of Object.entries(bindings)) {
    if (!path.startsWith(prefix)) { out[path] = expr; continue }
    const rest = path.slice(prefix.length)
    const dot = rest.indexOf('.')
    const idx = Number(dot === -1 ? rest : rest.slice(0, dot))
    if (!Number.isInteger(idx)) { out[path] = expr; continue }
    const n = toNew.get(idx)
    if (n === undefined) continue
    out[`${prefix}${n}${dot === -1 ? '' : rest.slice(dot)}`] = expr
  }
  return Object.keys(out).length ? out : undefined
}

// The `order` for moving item `idx` one step, or for removing it.
export function moveOrder(len: number, idx: number, dir: -1 | 1): number[] {
  const order = Array.from({ length: len }, (_, k) => k)
  const j = idx + dir
  if (j < 0 || j >= len) return order
  ;[order[idx], order[j]] = [order[j], order[idx]]
  return order
}
export function removeOrder(len: number, idx: number): number[] {
  return Array.from({ length: len }, (_, k) => k).filter((k) => k !== idx)
}
